// ui/src/components/ErrorBoundary.jsx
import React from 'react';
import { ErrorBoundary as HighlightErrorBoundary } from '@highlight-run/react';
import { H } from 'highlight.run';

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
          <svg
            className="w-6 h-6 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
          Something went wrong
        </h2>
        
        <p className="text-gray-600 text-center mb-6">
          We've encountered an unexpected error. Our team has been notified and will investigate the issue.
        </p>
        
        <div className="space-y-3">
          <button
            onClick={resetErrorBoundary}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reload page
          </button>
        </div>
        
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-6">
            <summary className="text-sm text-gray-500 cursor-pointer">
              Error details (development only)
            </summary>
            <pre className="mt-2 text-xs text-gray-600 bg-gray-100 p-3 rounded overflow-auto">
              {error.message}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

const ErrorBoundary = ({ children }) => {
  return (
    <HighlightErrorBoundary
      showDialog={false}
      fallback={ErrorFallback}
      onError={(error, errorInfo) => {
        // Forward to Highlight explicitly to ensure capture
        try {
          H.consumeError(error, { type: 'React.ErrorBoundary' });
        } catch {}
        // Additional console logging (visible in dev)
        console.error('Error caught by boundary:', error, errorInfo);
      }}
    >
      {children}
    </HighlightErrorBoundary>
  );
};

export default ErrorBoundary;
